// One place that decides how Fellowship moves (design/DIRECTION.md, "Motion").
// - LazyMotion: components use `m.*` (a few kB); the animation features arrive in their own chunk.
// - MotionConfig reducedMotion="user": under prefers-reduced-motion, Motion drops transforms and
//   layout movement and keeps opacity; the default transition becomes iOS's `fade` (0.2s easeOut),
//   so what would have moved crossfades instead of jumping (the iOS rule: never nothing).
// - The springs and curves are the iOS app's own tokens (src/motion/tokens.ts, FW-010); the house
//   CSS curves stay here as EASE and DUR for the few tweens that are the web's own.
import { LazyMotion, MotionConfig, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";
import { MOTION } from "./tokens";

const loadFeatures = () => import("./features").then((m) => m.default);

/** --ease-out, --ease-expo and --ease-roll from tokens.css, for Motion. */
export const EASE = {
  out: [0.22, 1, 0.36, 1],
  expo: [0.16, 1, 0.3, 1],
  roll: [0.625, 0.05, 0, 1],
  inOut: [0.65, 0, 0.35, 1],
} as const;

export const DUR = { press: 0.12, fast: 0.16, reveal: 0.24, move: 0.36, sheet: 0.42, page: 0.52, slow: 0.9 } as const;

export function MotionProvider({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <LazyMotion features={loadFeatures} strict>
      <MotionConfig reducedMotion="user" transition={reduced ? MOTION.fade : MOTION.settle}>
        {children}
      </MotionConfig>
    </LazyMotion>
  );
}
